import React, { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { green, grey } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { USE_PDF_SELECTOR, usePdf } from 'hooks/usePdf';
import {
  LenderActivitySubmissionsReportQueryResponse,
  LenderRetailActivityLenderRowViewModel,
} from '../../../api/types';
import { lenderActivityDashboardApi } from '../../../api';
import {
  LENDER_APPS_COLUMNS,
  exportLenderSubmissionsToCsv,
  getCountryLabel,
  getLenderAppsTotalRow,
  groupLenderRowsByCountry,
} from '../../../lib';
import { LenderAppsSorting, LenderRetailActivityReportInput } from '../../../types';
import { getCountryFlag } from './country';

interface Props {
  rows: LenderRetailActivityLenderRowViewModel[];
  input: LenderRetailActivityReportInput;
  loading?: boolean;
  sorting: LenderAppsSorting;
  onSortingChange: (nextValue: LenderAppsSorting) => void;
}

const cellSx = { border: '1px solid', borderColor: 'divider', py: 1, px: 1.5 } as const;
const headerCellSx = { ...cellSx, bgcolor: grey[300], fontWeight: 700 } as const;
const totalCellSx = { ...cellSx, bgcolor: green[50], color: green[800], fontWeight: 600 } as const;

export const LenderAppsTable = ({ rows, input, loading, sorting, onSortingChange }: Props) => {
  const classes = useStyles();
  const groups = groupLenderRowsByCountry(rows);
  const totalRow = getLenderAppsTotalRow(rows);
  const colSpan = LENDER_APPS_COLUMNS.length;
  const { contentRef, handleDownloadPdf } = usePdf();

  const [fetchSubmissionsReport, { loading: isExportingCsv }] =
    useLazyQuery<LenderActivitySubmissionsReportQueryResponse>(lenderActivityDashboardApi.getLenderSubmissionsReport, {
      context: { authRequired: true },
    });

  const handleSort = (key: string) => {
    const nextValue = sorting.key === key && sorting.value === 'desc' ? 'asc' : 'desc';
    onSortingChange({ key, value: nextValue });
  };

  const handleExportCsv = useCallback(async () => {
    const { data } = await fetchSubmissionsReport({ variables: { input } });
    const submissions = data?.lenderRetailActivity?.lenderSubmissionsReport?.entity ?? [];
    exportLenderSubmissionsToCsv(submissions);
  }, [fetchSubmissionsReport, input]);

  return (
    <Stack gap={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" component="span">
          Percentage of Lender financed amount
        </Typography>

        <Stack direction="row" gap={1} data-pdf-exclude>
          <Button size="small" variant="outlined" startIcon={<CloudDownloadIcon />} onClick={() => handleDownloadPdf()}>
            Export PDF
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            onClick={handleExportCsv}
            disabled={isExportingCsv}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>

      <Box ref={contentRef}>
        <TableContainer
          className={`tableContainer ${classes.pdfScope}`}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {LENDER_APPS_COLUMNS.map(column => {
                  const isActive = sorting.key === column.key;

                  return (
                    <TableCell
                      key={column.key}
                      align={column.align}
                      sx={headerCellSx}
                      sortDirection={isActive ? sorting.value : false}
                    >
                      <TableSortLabel
                        active={isActive}
                        direction={isActive ? sorting.value : 'asc'}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.header}
                      </TableSortLabel>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && !groups.length ? (
                <TableRow>
                  <TableCell colSpan={colSpan} sx={cellSx}>
                    <Skeleton height={160} />
                  </TableCell>
                </TableRow>
              ) : !groups.length ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={cellSx}>
                    <Typography>No data</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {groups.map(group => {
                    const flag = getCountryFlag(group.country);

                    return (
                      <React.Fragment key={group.country}>
                        <TableRow>
                          <TableCell colSpan={colSpan} sx={{ ...cellSx, bgcolor: grey[50] }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                              {flag && <Box component={flag} sx={{ width: 18, height: 18 }} />}
                              <Typography variant="subtitle2" component="span">
                                {getCountryLabel(group.country)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>

                        {group.rows.map(row => (
                          <TableRow key={row.id}>
                            {LENDER_APPS_COLUMNS.map(column => (
                              <TableCell key={column.key} align={column.align} sx={cellSx}>
                                {column.render(row)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}

                  {totalRow && (
                    <TableRow>
                      {LENDER_APPS_COLUMNS.map(column => (
                        <TableCell key={column.key} align={column.align} sx={totalCellSx}>
                          {column.render(totalRow)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
};

const useStyles = makeStyles({
  pdfScope: {
    [`${USE_PDF_SELECTOR} &`]: {
      '& td': { padding: '4px 6px !important', fontSize: '11px !important' },
      '& th': { padding: '4px 6px !important', fontSize: '11px !important' },
      '& .MuiTypography-root': { fontSize: '11px !important' },
    },
  },
});
